import { StreamingChatStatus } from "@storytell/streaming";
import { createEffect, createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import { type ChatRuntime, useChatRuntime } from "../runtime/ChatRuntime";
import { createApprovalTracker } from "./approvalContinuation";

type ToolActionDecision = "approve" | "approve_always" | "reject";
// An approval resolves to one of the accepted states; the side effect runs in an
// async execute job, so the synchronous response is `queued` rather than a
// provider outcome. `failed` is the one accepted state that carries an error.
type ToolActionStatus = "queued" | "executing" | "executed" | "rejected" | "failed";

// The full lifecycle the status endpoint reports; the confirm response only
// returns the transition it just made.
export type ToolActionServerStatus =
  | "pending"
  | "queued"
  | "executing"
  | "executed"
  | "rejected"
  | "failed"
  | "blocked";

const SERVER_STATUSES: ReadonlySet<ToolActionServerStatus> = new Set([
  "pending",
  "queued",
  "executing",
  "executed",
  "rejected",
  "failed",
  "blocked",
]);

const isServerStatus = (v: unknown): v is ToolActionServerStatus =>
  typeof v === "string" && SERVER_STATUSES.has(v as ToolActionServerStatus);

// The approve path returns `queued` (the side effect runs later), so gating
// success on a provider-terminal status would strand every approval as a
// non-error failure with the card still showing its Approve controls.
const isSettledStatus = (v: unknown): v is ToolActionStatus =>
  v === "queued" || v === "executing" || v === "executed" || v === "rejected" || v === "failed";

// bluesky's runtime rejects with a client Error carrying the backend envelope's
// string `code` and the HTTP status; chief's Wails bindings reject with plain
// strings. Map both onto the {code, message} shape extractErrorInfo reads.
const normalizeError = (e: unknown): { code: string | number | undefined; message: string | undefined } => {
  if (e instanceof Error) {
    const meta = e as Error & { code?: string; status?: number };
    return { code: meta.code ?? meta.status, message: e.message };
  }
  if (e && typeof e === "object") {
    const obj = e as { code?: string | number; message?: string };
    return { code: obj.code, message: obj.message };
  }
  return {
    code: undefined,
    message: typeof e === "string" || typeof e === "number" || typeof e === "boolean" ? String(e) : undefined,
  };
};

interface ToolActionInfo {
  status: ToolActionServerStatus;
  // Absent on a host whose backend predates the flag; consumers treat that as
  // approvable so an older deployment keeps its buttons.
  approvable?: boolean;
  url?: string;
  error?: string;
  executedAt?: string;
  reconnect?: boolean;
  // Set only when the server allows approve_always for the reading user.
  alwaysApproveOp?: string;
  autoApproved?: boolean;
}

// Confirm cards persist as 'pending' and are never re-emitted with their
// outcome, so cards reopened after a reload resolve their real state from this
// per-chat batch instead of each POSTing a confirm that 409s. The cache is
// module-level so every card in a chat shares one lookup (the web app gets the
// same dedup from a shared TanStack query key).
const STALE_MS = 30_000;

interface CacheEntry {
  at: number;
  data: Map<string, ToolActionInfo> | null;
  failed: boolean;
  inflight: boolean;
}

const statusCache = new Map<string, CacheEntry>();
const [cacheVersion, setCacheVersion] = createSignal(0);

const bumpCache = () => setCacheVersion((v) => v + 1);

function ensureFetched(rt: ChatRuntime, chatId: string): void {
  const existing = statusCache.get(chatId);
  if (existing && (existing.inflight || Date.now() - existing.at < STALE_MS)) return;

  if (!rt.actions.fetchToolActions) {
    // No lookup on this host: settle immediately so consumers fall back to the
    // payload's own status instead of waiting forever.
    statusCache.set(chatId, { at: Date.now(), data: new Map(), failed: false, inflight: false });
    bumpCache();
    return;
  }

  void fetchStatuses(rt, chatId);
}

async function fetchStatuses(rt: ChatRuntime, chatId: string): Promise<void> {
  const fetchToolActions = rt.actions.fetchToolActions;
  if (!fetchToolActions) return;
  const existing = statusCache.get(chatId);
  if (existing?.inflight) return;

  const entry: CacheEntry = { at: Date.now(), data: existing?.data ?? null, failed: false, inflight: true };
  statusCache.set(chatId, entry);
  bumpCache();

  try {
    const actions = await fetchToolActions(chatId);
    const data = new Map<string, ToolActionInfo>();
    for (const action of actions) {
      if (typeof action.id === "string" && isServerStatus(action.status)) {
        const info: ToolActionInfo = { status: action.status };
        if (typeof action.approvable === "boolean") info.approvable = action.approvable;
        if (typeof action.url === "string") info.url = action.url;
        if (typeof action.error === "string") info.error = action.error;
        if (typeof action.executedAt === "string") info.executedAt = action.executedAt;
        if (action.reconnect === true) info.reconnect = true;
        if (action.alwaysApprovable === true && typeof action.op === "string") info.alwaysApproveOp = action.op;
        if (action.autoApproved === true) info.autoApproved = true;
        data.set(action.id, info);
      }
    }
    entry.data = data;
    entry.failed = false;
  } catch {
    entry.failed = true;
  } finally {
    entry.inflight = false;
    entry.at = Date.now();
    bumpCache();
  }
}

// The execute job reports nothing back to the client, so an approval made here
// polls the list until every action it queued is terminal. One approval is one
// job attempt of up to five minutes; the cap covers that plus a retry. Most
// approvals settle in seconds, so the fast interval only lasts the first half
// minute; past that the wait is a slow job and the card can lag five seconds.
const POLL_MS = 2_000;
const POLL_SLOW_AFTER_MS = 30_000;
const POLL_SLOW_MS = 5_000;
const POLL_MAX_MS = 10 * 60_000;

const approvals = createApprovalTracker();
const polling = new Set<string>();

const cachedStatus = (chatId: string) => (actionId: string) => statusCache.get(chatId)?.data?.get(actionId)?.status;

function pollUntilSettled(rt: ChatRuntime, chatId: string): void {
  if (polling.has(chatId)) return;
  polling.add(chatId);
  const startedAt = Date.now();
  const tick = () => {
    if (!approvals.hasInFlight(chatId, cachedStatus(chatId)) || Date.now() - startedAt > POLL_MAX_MS) {
      polling.delete(chatId);
      return;
    }
    void fetchStatuses(rt, chatId).finally(() =>
      setTimeout(tick, Date.now() - startedAt > POLL_SLOW_AFTER_MS ? POLL_SLOW_MS : POLL_MS)
    );
  };
  setTimeout(tick, POLL_MS);
}

/**
 * Real statuses for a chat's confirm cards, shared by every card in the chat.
 * The lookup only enriches reopened cards' status, so a failure is non-fatal
 * and never retries within the staleness window.
 */
export function useChatToolActions(chatId: () => string | undefined, hasPendingCards: () => boolean) {
  const rt = useChatRuntime();

  // The fetch is a side effect, so it runs in an effect (client-only) rather
  // than inside the accessor that reads the cache.
  createEffect(() => {
    cacheVersion();
    const id = chatId();
    if (isServer || !id || !hasPendingCards()) return;
    ensureFetched(rt, id);
  });

  // Every card in the chat runs this; settling forgets the batch, so the first
  // run to see it terminal is the only one that continues.
  createEffect(() => {
    cacheVersion();
    const id = chatId();
    if (isServer || !id || !rt.actions.continueChat) return;
    const streaming = rt.store.chatStatus?.(id) === StreamingChatStatus.GENERATING;
    if (approvals.settle(id, cachedStatus(id), streaming) === "continue") {
      rt.actions.continueChat({ chatId: id, text: rt.t("confirm_action_card.continue_after_approval") });
    }
  });

  // A plain accessor, deliberately not a memo: a resolved fetch mutates the
  // cache entry in place, so a memo would keep returning the same reference
  // and never notify dependents — statuses would stay "loading" until a
  // remount. Reading cacheVersion here makes every consumer re-evaluate.
  const entry = () => {
    cacheVersion();
    const id = chatId();
    if (!id || !hasPendingCards()) return undefined;
    return statusCache.get(id);
  };

  return {
    // Only an approval recorded here can continue the chat once it executes.
    trackApproval: (actionId: string): void => {
      const id = chatId();
      if (!id || !rt.actions.continueChat || !rt.actions.fetchToolActions) return;
      approvals.track(id, actionId);
      pollUntilSettled(rt, id);
    },
    // Re-reads the chat's statuses ahead of the staleness window, for a card the
    // server just told is out of date.
    refresh: (): void => {
      const id = chatId();
      if (id) void fetchStatuses(rt, id);
    },
    statusOf: (actionId: string | undefined): ToolActionServerStatus | undefined =>
      actionId ? entry()?.data?.get(actionId)?.status : undefined,
    // Undefined until the lookup settles, so a caller can tell "not yet known"
    // from a resolved no.
    approvableOf: (actionId: string | undefined): boolean | undefined =>
      actionId ? entry()?.data?.get(actionId)?.approvable : undefined,
    alwaysApproveOpOf: (actionId: string | undefined): string | undefined =>
      actionId ? entry()?.data?.get(actionId)?.alwaysApproveOp : undefined,
    autoApprovedOf: (actionId: string | undefined): boolean =>
      actionId ? entry()?.data?.get(actionId)?.autoApproved === true : false,
    urlOf: (actionId: string | undefined): string | undefined =>
      actionId ? entry()?.data?.get(actionId)?.url : undefined,
    errorOf: (actionId: string | undefined): string | undefined =>
      actionId ? entry()?.data?.get(actionId)?.error : undefined,
    reconnectOf: (actionId: string | undefined): boolean =>
      actionId ? entry()?.data?.get(actionId)?.reconnect === true : false,
    executedAtOf: (actionId: string | undefined): string | undefined =>
      actionId ? entry()?.data?.get(actionId)?.executedAt : undefined,
    isLoading: () => {
      const e = entry();
      return !!e && e.data === null && !e.failed;
    },
    // True once the lookup produced data or an error; before the first fetch
    // resolves (or pre-auth) it hasn't.
    isSettled: () => {
      const e = entry();
      return !!e && (e.data !== null || e.failed);
    },
  };
}

type ConfirmSubmitResult =
  | { ok: true; value: ToolActionStatus }
  | { ok: false; error: { code: string | number | undefined; message: string | undefined } };

/**
 * Submits the human decision through the host's `confirmAction` seam. The
 * action only runs server-side on approval; the model is never in the send
 * path. `confirm` resolves to the new settled status; the normalized error is
 * exposed via the `error` signal for callers to translate.
 */
export function useToolActionConfirm() {
  const rt = useChatRuntime();

  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [error, setError] = createSignal<unknown>(null);

  const confirm = async (
    actionId: string,
    decision: ToolActionDecision,
    notifyAttendees: boolean,
    editedParams?: Record<string, unknown>
  ): Promise<ConfirmSubmitResult> => {
    const confirmAction = rt.actions.confirmAction;
    if (isServer || !confirmAction) {
      return { ok: false, error: { code: undefined, message: undefined } };
    }

    setError(null);
    setIsSubmitting(true);
    rt.actions.trackFeature?.({ feature: "chat.tool_action", action: editedParams ? "edit" : decision, via: "button" });

    try {
      const status = await confirmAction({ actionId, decision, notifyAttendees, edited: editedParams });
      if (!isSettledStatus(status)) {
        const normalized = { code: undefined, message: undefined };
        setError(normalized);
        return { ok: false, error: normalized };
      }
      return { ok: true, value: status };
    } catch (e) {
      const normalized = normalizeError(e);
      setError(normalized);
      return { ok: false, error: normalized };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { confirm, isSubmitting, error };
}
